import React from 'react';
import classNames from 'classnames';
import { TextLink, TextLinkBrandColors, CtaStyle } from '@hallmark/web.core.buttons.text-link';
import { Headline } from '@hallmark/web.core.typography.headline';
import { Typography, TypographyVariants } from '@hallmark/web.core.typography.typography';
import { setHtmlId } from '@hallmark/web.util.set-html-id';
import {
  TextBlockProps,
  PresetLayout,
  PresetLayoutDetail,
  TypographyLayout,
  TextWidth,
  ContentAlign,
  TextBlockCtaStyleDetail,
  TabsBrandColors,
} from '../tabs-types';
import styles from '../tabs.module.scss';
import { Eyebrow } from './eyebrow';

export const TextBlock = ({
  anchorId,
  bottomPadding,
  backgroundColor,
  presetLayout,
  eyebrow,
  headline,
  headlineColor,
  headlineStyle,
  bodyCopy,
  primaryCta,
  primaryCtaStyle,
  testId,
}: TextBlockProps): React.ReactElement => {
  // Defaults
  const presetLayoutDetails: Record<string, PresetLayoutDetail> = {
    [PresetLayout.LargeCentered]: {
      typographyLayout: TypographyLayout.TypographyCenter,
      textWidth: TextWidth.TextBlockLarge,
      contentAlign: ContentAlign.AlignCenter,
      eyebrow: eyebrow || '',
      headline: headline || '',
      headlineColor: headlineColor || TabsBrandColors.Purple,
      headlineStyle,
      bodyCopy,
      primaryCtaStyle,
      bottomPadding,
    },
    [PresetLayout.LargeLeft]: {
      typographyLayout: TypographyLayout.TypographyLeft,
      textWidth: TextWidth.TextBlockLarge,
      contentAlign: ContentAlign.AlignLeft,
      eyebrow: eyebrow || '',
      headline: headline || '',
      headlineColor: headlineColor || TabsBrandColors.Purple,
      headlineStyle,
      bodyCopy,
      primaryCtaStyle,
      bottomPadding,
    },
    [PresetLayout.SmallCentered]: {
      typographyLayout: TypographyLayout.TypographyCenter,
      textWidth: TextWidth.TextBlockSmall,
      contentAlign: ContentAlign.AlignCenter,
      eyebrow: eyebrow || '',
      headline: headline || '',
      headlineColor: headlineColor || TabsBrandColors.Purple,
      headlineStyle,
      bodyCopy,
      primaryCtaStyle,
      bottomPadding,
    },
    [PresetLayout.SmallLeft]: {
      typographyLayout: TypographyLayout.TypographyLeft,
      textWidth: TextWidth.TextBlockSmall,
      contentAlign: ContentAlign.AlignLeft,
      eyebrow: eyebrow || '',
      headline: headline || '',
      headlineColor: headlineColor || TabsBrandColors.Purple,
      headlineStyle,
      bodyCopy,
      primaryCtaStyle,
      bottomPadding,
    },
  };

  const model = presetLayoutDetails[`${presetLayout}`];

  const classes = classNames(
    styles.textBlock,
    styles[model.textWidth],
    styles[`textBlock--padding-bottom-${model && model.bottomPadding && model.bottomPadding.toLowerCase()}`],
    {
      [styles[`background-color-${backgroundColor || ''.replace(' ', '-')}`]]: backgroundColor,
      [styles['textBlock--pt-24']]: backgroundColor,
    },
  );

  const contentClasses = classNames(styles.content, styles[model.contentAlign]);

  const wrapperClasses = classNames(styles.typoWrapper, styles[model.typographyLayout]);

  const currentCtaStyle = model.primaryCtaStyle;

  const ctaBackgroundColor = primaryCta && primaryCta.backgroundColor ? primaryCta.backgroundColor : '';

  const textColor = primaryCta?.textColor || '';

  const ctaProps: Record<string, TextBlockCtaStyleDetail> = {
    [CtaStyle.Secondary]: {
      backgroundColor: ctaBackgroundColor || TextLinkBrandColors.White,
      btnStyle: CtaStyle.Secondary,
      textColor: textColor || TextLinkBrandColors.Purple,
    },
    [CtaStyle.Primary]: {
      backgroundColor: ctaBackgroundColor || TextLinkBrandColors.Purple,
      btnStyle: CtaStyle.Primary,
      textColor: textColor || TextLinkBrandColors.White,
    },
  };

  const ctaClasses = classNames(
    styles.ctaButton,
    styles['btn-fixed'],
    currentCtaStyle && styles[`btn-${ctaProps[`${currentCtaStyle}`].btnStyle}`],
    currentCtaStyle && styles[`background-color-${ctaProps[`${currentCtaStyle}`].backgroundColor}`],
  );

  return (
    <section {...setHtmlId(anchorId || '')} className={classes} data-testid={testId}>
      <div className={contentClasses}>
        <div className={wrapperClasses}>
          {model.eyebrow && <Eyebrow text={model.eyebrow} />}
          {model.headline && (
            <Headline color={model.headlineColor} textLine1={model.headline} variant={model.headlineStyle} />
          )}
          {model.bodyCopy && (
            <Typography variant={TypographyVariants.Body} addClass={styles.description}>
              test
            </Typography>
          )}
          <div className={styles['cta-wrapper']}>
            {primaryCta && (
              <TextLink
                addClass={ctaClasses}
                color={currentCtaStyle && ctaProps[`${currentCtaStyle}`].textColor}
                ctaStyle={primaryCtaStyle}
                defaultClassOverride="btn"
                title={primaryCta.title}
                typographyVariant={TypographyVariants.CTA}
                url={primaryCta.url}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
